export type SolarSetupGroupId =
  | `contract`
  | `home-and-utility-info`
  | `pricing`
  | `product-offering`
  | `share`
  | `system-placement`

export type SolarSetupStep =
  | `adders`
  | `compute`
  | `consumption`
  | `contract`
  | `design`
  | `equipment`
  | `financing-status`
  | `greenbutton`
  | `hdm`
  | `incentives`
  | `loan-product`
  | `site`
  | `system-pricing`
  | `utility`
  | `vpp-programs`
  | `warranty`

export const SOLAR_SETUP_STEP_LABELS: Record<SolarSetupStep, string> = {
  adders: `Adders`,
  compute: `Compute`,
  consumption: `Consumption`,
  contract: `Contract status`,
  design: `System Design`,
  equipment: `Finance Partner & Equipment`,
  [`financing-status`]: `Financing Status`,
  greenbutton: `Green Button Data`,
  hdm: `HDM`,
  incentives: `Incentives`,
  [`loan-product`]: `Loan Product`,
  site: `Site Information`,
  [`system-pricing`]: `System Pricing`,
  utility: `Utility Company & Rate`,
  [`vpp-programs`]: `VPP Programs`,
  warranty: `Protection Plans`,
}

/** A step's drawer title, where it differs from the rail label (V1's modal title). */
export const SOLAR_SETUP_DRAWER_TITLES: Record<SolarSetupStep, string> = {
  ...SOLAR_SETUP_STEP_LABELS,
  equipment: `Finance Plan and Equipment Selection`,
  compute: `AI Compute`,
  hdm: `HDM Quote`,
}

/** A step opens its drawer at `/solar-proposal/:id/:step`. */
export type SolarSetupStepPart = {
  readonly kind: `step`
  readonly id: SolarSetupStep
  readonly label: string
  /** Listed but disabled until stored System Pricing exists. */
  readonly requiresPricing?: true
  /** Listed only while the plan finances the system (Credit Human, Concert). */
  readonly requiresLoanProduct?: true
}

/** Links to a routed drawer outside the step list, as Battery's Share does. */
export type SolarSetupRoutePart = {
  readonly kind: `route`
  readonly id: `share`
  readonly label: string
}

/** Runs from the rail in place, with no drawer: Export To PDF. */
export type SolarSetupActionPart = {
  readonly kind: `action`
  readonly id: `export-pdf`
  readonly label: string
}

export type SolarSetupPart =
  | SolarSetupActionPart
  | SolarSetupRoutePart
  | SolarSetupStepPart

export type SolarSetupGroup = {
  readonly id: SolarSetupGroupId
  readonly label: string
  /** The group's steps and actions, in rail order. */
  readonly parts: readonly SolarSetupPart[]
}

const HOME_AND_UTILITY: SolarSetupGroup = {
  id: `home-and-utility-info`,
  label: `Home & Utility Info`,
  parts: [
    { kind: `step`, id: `site`, label: SOLAR_SETUP_STEP_LABELS.site },
    { kind: `step`, id: `utility`, label: SOLAR_SETUP_STEP_LABELS.utility },
    {
      kind: `step`,
      id: `consumption`,
      label: SOLAR_SETUP_STEP_LABELS.consumption,
    },
    {
      kind: `step`,
      id: `greenbutton`,
      label: SOLAR_SETUP_STEP_LABELS.greenbutton,
    },
  ],
}

const PRODUCT_OFFERING: SolarSetupGroup = {
  id: `product-offering`,
  label: `Product Offering`,
  parts: [
    { kind: `step`, id: `equipment`, label: SOLAR_SETUP_STEP_LABELS.equipment },
    // Always listed, as Battery's is: Cash opens it to a note that no Loan
    // Product applies.
    {
      kind: `step`,
      id: `loan-product`,
      label: SOLAR_SETUP_STEP_LABELS[`loan-product`],
    },
    { kind: `step`, id: `compute`, label: SOLAR_SETUP_STEP_LABELS.compute },
  ],
}

const SYSTEM_PLACEMENT: SolarSetupGroup = {
  id: `system-placement`,
  label: `System Placement`,
  parts: [{ kind: `step`, id: `design`, label: SOLAR_SETUP_STEP_LABELS.design }],
}

/** Battery's Pricing order: the inputs, the price, then what reads it. */
const PRICING: SolarSetupGroup = {
  id: `pricing`,
  label: `Pricing`,
  parts: [
    { kind: `step`, id: `adders`, label: SOLAR_SETUP_STEP_LABELS.adders },
    { kind: `step`, id: `warranty`, label: SOLAR_SETUP_STEP_LABELS.warranty },
    {
      kind: `step`,
      id: `system-pricing`,
      label: SOLAR_SETUP_STEP_LABELS[`system-pricing`],
    },
    {
      kind: `step`,
      id: `incentives`,
      label: SOLAR_SETUP_STEP_LABELS.incentives,
      requiresPricing: true,
    },
    {
      kind: `step`,
      id: `vpp-programs`,
      label: SOLAR_SETUP_STEP_LABELS[`vpp-programs`],
    },
  ],
}

/** Contract status is the shared Contract drawer, which keeps the rail open
 * beside it as Battery's does. Financing Status sits beside it: the finance
 * partner's application gates the contract, and Contract status links into
 * it. HDM is where everything HDM happens, starting with creating a quote. */
const CONTRACT: SolarSetupGroup = {
  id: `contract`,
  label: `Contract`,
  parts: [
    { kind: `step`, id: `contract`, label: SOLAR_SETUP_STEP_LABELS.contract },
    {
      kind: `step`,
      id: `financing-status`,
      label: SOLAR_SETUP_STEP_LABELS[`financing-status`],
      requiresLoanProduct: true,
    },
    { kind: `step`, id: `hdm`, label: SOLAR_SETUP_STEP_LABELS.hdm },
  ],
}

const SHARE: SolarSetupGroup = {
  id: `share`,
  label: `Share`,
  parts: [
    { kind: `route`, id: `share`, label: `Share` },
    // Generates the PDF in place: no drawer, and no published link needed,
    // so it sits beside Share rather than inside it.
    { kind: `action`, id: `export-pdf`, label: `Export To PDF` },
  ],
}

/** The Setup rail's top-level groups, in rail order. */
export const SOLAR_SETUP_GROUPS: readonly SolarSetupGroup[] = [
  HOME_AND_UTILITY,
  PRODUCT_OFFERING,
  SYSTEM_PLACEMENT,
  PRICING,
  CONTRACT,
  SHARE,
]

/** The group whose sub-list holds each step, which its drawer closes back to. */
const GROUP_BY_STEP: Record<SolarSetupStep, SolarSetupGroup> = {
  adders: PRICING,
  compute: PRODUCT_OFFERING,
  consumption: HOME_AND_UTILITY,
  contract: CONTRACT,
  design: SYSTEM_PLACEMENT,
  equipment: PRODUCT_OFFERING,
  [`financing-status`]: CONTRACT,
  greenbutton: HOME_AND_UTILITY,
  hdm: CONTRACT,
  incentives: PRICING,
  [`loan-product`]: PRODUCT_OFFERING,
  site: HOME_AND_UTILITY,
  [`system-pricing`]: PRICING,
  utility: HOME_AND_UTILITY,
  [`vpp-programs`]: PRICING,
  warranty: PRICING,
}

/** A step whose drawer brings its own full-width chrome, which the rail yields to. */
export const solarStepFillsViewport = (step: SolarSetupStep): boolean =>
  step === `greenbutton`

const isSolarSetupStep = (value: string): value is SolarSetupStep =>
  Object.hasOwn(GROUP_BY_STEP, value)

/** The group's parts the plan shows: Cash hides the Financing Status step. */
export const visibleSolarSetupParts = (
  group: SolarSetupGroup,
  { loanProductRequired }: { loanProductRequired: boolean },
): readonly SolarSetupPart[] =>
  group.parts.filter(
    (part) =>
      part.kind !== `step` || !part.requiresLoanProduct || loanProductRequired,
  )

export const solarSetupGroupForStep = (step: SolarSetupStep): SolarSetupGroup =>
  GROUP_BY_STEP[step]

export const isSolarSetupGroupId = (
  value: string | undefined,
): value is SolarSetupGroupId =>
  SOLAR_SETUP_GROUPS.some((group) => group.id === value)

export const solarProposalPath = (proposalId: string): string =>
  `/solar-proposal/${proposalId}`

export const solarSetupMenuPath = (proposalId: string): string =>
  `${solarProposalPath(proposalId)}/setup`

export const solarSetupGroupPath = (
  proposalId: string,
  groupId: SolarSetupGroupId,
): string => `${solarSetupMenuPath(proposalId)}/${groupId}`

/** The V2 desktop designer, full screen outside the proposal shell. */
export const solarDesignerPath = (proposalId: string): string =>
  `${solarProposalPath(proposalId)}/designer`

/** The Share drawer, a routed sibling of the step drawers. */
export const solarSharePath = (proposalId: string): string =>
  `${solarProposalPath(proposalId)}/share`

/** Whether the path is the Share drawer's. A trailing slash still counts. */
export const isSolarSharePath = (
  pathname: string,
  proposalId: string,
): boolean => pathname.replace(/\/+$/, ``) === solarSharePath(proposalId)

/** The print route Chromium loads; mirrors the gateway's SOLAR_PRINT_PATH. */
export const SOLAR_PRINT_PRESENTATION_PATH = `/print/solar-presentation`

export const solarSetupStepPath = (
  proposalId: string,
  step: SolarSetupStep,
): string => `${solarProposalPath(proposalId)}/${step}`

/** The Contract drawer's route, which a read-only viewer may also open. */
export const solarContractPath = (proposalId: string): string =>
  solarSetupStepPath(proposalId, `contract`)

/** The step whose drawer the path opens. A trailing slash still counts. */
export const solarSetupStepFromPath = (
  pathname: string,
  proposalId: string,
): SolarSetupStep | null => {
  const path = pathname.replace(/\/+$/, ``)
  const proposalPath = solarProposalPath(proposalId)
  if (!path.startsWith(`${proposalPath}/`)) {
    return null
  }
  const tail = path.slice(proposalPath.length + 1)
  return isSolarSetupStep(tail) ? tail : null
}

/** What follows `/setup` in the path: `[]` for the rail itself, `[groupId]`
 * for a group. Null off the rail. A trailing slash still counts. */
const setupTail = (pathname: string, proposalId: string): string[] | null => {
  const path = pathname.replace(/\/+$/, ``)
  const setupPath = solarSetupMenuPath(proposalId)
  if (path === setupPath) {
    return []
  }
  return path.startsWith(`${setupPath}/`)
    ? path.slice(setupPath.length + 1).split(`/`)
    : null
}

/** The group in the URL, when the path is a known group's. */
export const solarSetupGroupFromPath = (
  pathname: string,
  proposalId: string,
): SolarSetupGroupId | null => {
  const tail = setupTail(pathname, proposalId)
  return tail?.length === 1 && isSolarSetupGroupId(tail[0]) ? tail[0] : null
}

/** The group the rail expands: the one in the URL, an open step's, or Share's
 * while its drawer is showing. */
export const solarActiveSetupGroupFromPath = (
  pathname: string,
  proposalId: string,
): SolarSetupGroupId | null => {
  if (isSolarSharePath(pathname, proposalId)) {
    return SHARE.id
  }
  const step = solarSetupStepFromPath(pathname, proposalId)
  return step
    ? solarSetupGroupForStep(step).id
    : solarSetupGroupFromPath(pathname, proposalId)
}

/** Open on `/setup`, on a known group's `/setup/:groupId`, and under a step's
 * or the Share drawer, as Battery's rail stays open beside its drawers. */
export const isSolarSetupMenuOpen = (
  pathname: string,
  proposalId: string,
): boolean =>
  setupTail(pathname, proposalId)?.length === 0 ||
  solarActiveSetupGroupFromPath(pathname, proposalId) != null
